import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import config from "../config/config";

const DynamicNavbar = () => {
  const [navigationData, setNavigationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownStates, setDropdownStates] = useState({});

  // Fallback navigation if API fails
  const getFallbackNavigation = () => {
    return [
      {
        id: 1,
        title: "Home",
        slug: "home",
        url: "/",
        icon: "fas fa-home",
        has_page: false,
        has_children: false,
        order_index: 1,
        is_active: true,
        parent_id: null,
      },
      {
        id: 2,
        title: "About Us",
        slug: "about-us",
        url: "/about-us",
        icon: "fas fa-info-circle",
        has_page: false,
        has_children: true,
        order_index: 2,
        is_active: true,
        parent_id: null,
      },
      {
        id: 3,
        title: "Services",
        slug: "services",
        url: "/services",
        icon: "fas fa-cogs",
        has_page: true,
        has_children: false,
        order_index: 3,
        is_active: true,
        parent_id: null,
      },
      {
        id: 4,
        title: "Products",
        slug: "products",
        url: "/products",
        icon: "fas fa-box",
        has_page: true,
        has_children: false,
        order_index: 4,
        is_active: true,
        parent_id: null,
      },
      {
        id: 5,
        title: "Contact Us",
        slug: "contact-us",
        url: "/contact-us",
        icon: "fas fa-envelope",
        has_page: false,
        has_children: false,
        order_index: 5,
        is_active: true,
        parent_id: null,
      },
      {
        id: 6,
        title: "Careers",
        slug: "careers",
        url: "/careers",
        icon: "fas fa-briefcase",
        has_page: false,
        has_children: false,
        order_index: 6,
        is_active: true,
        parent_id: null,
      },
    ];
  };

  useEffect(() => {
    const fetchNavigation = async () => {
      try {
        console.log("Fetching navigation data from database...");
        
        // Try to fetch from database first
        const apiUrl = config.getApiUrl("get_navigation.php");
        console.log("API URL:", apiUrl);

        // Set a timeout for the fetch request
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

        const response = await fetch(apiUrl, {
          method: "GET",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        console.log("Response status:", response.status);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Navigation data received:", data);

        if (data.success && data.navigation && data.navigation.length > 0) {
          setNavigationData(data.navigation);
          console.log("Navigation data set from database:", data.navigation);
        } else {
          throw new Error("No navigation data received from database");
        }
      } catch (error) {
        console.error("Error fetching navigation from database:", error);
        setError(`Database Error: ${error.message}`);
        // Fallback to basic navigation only if database fails
        console.log("Database failed, using fallback navigation");
        setNavigationData(getFallbackNavigation());
      } finally {
        setLoading(false);
      }
    };

    // Use fallback navigation immediately
    fetchNavigation();
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleDropdown = (itemId) => {
    setDropdownStates((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const generatePath = (item, parent = null) => {
    if (item.slug === "home") {
      return "/";
    }

    // Check if item has external URL
    if (item.external_url) {
      return item.external_url;
    }

    // For special pages that have their own components (nav_has_page = false)
    if (["about-us", "contact-us", "careers"].includes(item.slug)) {
      return `/${item.slug}`;
    }

    // Handle About us dropdown items with hash links
    if (parent && parent.slug === "about-us") {
      const hashMap = {
        "vision-mission": "#vision-and-mission",
        "our-team": "#our-team",
        infrastructure: "#infrastructure",
        "company-overview": "#company-overview",
      };
      return `/about-us${hashMap[item.slug] || ""}`;
    }

    // For other items without pages
    if (!item.has_page) {
      return `/${item.slug}`;
    }

    // For pages that use the dynamic Page.js component (nav_has_page = true)
    return `/page/${item.slug}`;
  };

  const renderNavItem = (item, level = 1, parent = null) => {
    const hasChildren = item.children && item.children.length > 0;
    const path = generatePath(item, parent);

    if (level === 1) {
      // Top-level navigation items
      return (
        <li key={item.id} className={hasChildren ? "dropdown-parent" : ""}>
          {hasChildren ? (
            <div className="dropdown-wrapper">
              <Link to={path} className="dropdown-main-link" onClick={() => setMobileMenuOpen(false)}>
                {item.icon && <i className={`${item.icon} me-1`}></i>}
                {item.title}
              </Link>
              <button
                type="button"
                className="dropdown-toggle dropdown-arrow-only"
                onClick={(e) => {
                  e.preventDefault();
                  toggleDropdown(item.id);
                }}
              >
                <i className={`fas fa-chevron-${dropdownStates[item.id] ? 'up' : 'down'}`}></i>
              </button>
            </div>
          ) : (
            <Link to={path} onClick={() => setMobileMenuOpen(false)}>
              {item.icon && <i className={`${item.icon} me-1`}></i>}
              {item.title}
            </Link>
          )}

          {hasChildren && (
            <ul
              className={`dropdown-menu ${
                dropdownStates[item.id] ? "show" : ""
              }`}
            >
              {item.children.map((child) => renderNavItem(child, 2, item))}
            </ul>
          )}
        </li>
      );
    } else if (level === 2) {
      // Dropdown items
      return (
        <li key={item.id} className={hasChildren ? "has-submenu" : ""}>
          <Link to={path} onClick={() => setMobileMenuOpen(false)}>
            {item.icon && <i className={item.icon}></i>} {item.title}
            {hasChildren && (
              <i className="fas fa-chevron-right submenu-arrow"></i>
            )}
          </Link>
          {hasChildren && (
            <ul className="submenu">
              {item.children.map((child) => renderNavItem(child, 3, item))}
            </ul>
          )}
        </li>
      );
    } else {
      // Submenu items (level 3)
      return (
        <li key={item.id}>
          <Link to={path} onClick={() => setMobileMenuOpen(false)}>
            {item.icon && <i className={item.icon}></i>} {item.title}
          </Link>
        </li>
      );
    }
  };

  if (loading) {
    return (
      <nav className="main-navbar">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <Link to="/" className="logo-container">
              <img
                src={config.getImageUrl("logo.png")}
                alt="NCG Corporate"
                className="logo-image"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "inline-block";
                }}
              />
              <span className="logo-text-fallback" style={{ display: "none" }}>
                NCG{" "}
                <span className="logo-gear">
                  <i className="fas fa-cog"></i>
                </span>
                <span className="logo-subtitle">CORPORATE</span>
              </span>
            </Link>
            <div className="nav-loading">
              <i className="fas fa-spinner fa-spin"></i> Loading navigation...
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="main-navbar">
      <div className="container">
        <div className="d-flex justify-content-between align-items-center">
          <Link to="/" className="logo-container">
            <img
              src={config.getImageUrl("logo.png")}
              alt="NCG Corporate"
              className="logo-image"
              onError={(e) => {
                console.log('Logo failed to load:', e.target.src);
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "inline-block";
              }}
              onLoad={() => {
                console.log('Logo loaded successfully:', config.getImageUrl("logo.png"));
              }}
            />
            <span className="logo-text-fallback" style={{ display: "none" }}>
              NCG{" "}
              <span className="logo-gear">
                <i className="fas fa-cog"></i>
              </span>
              <span className="logo-subtitle">CORPORATE</span>
            </span>
          </Link>

          <button
            className="mobile-toggle"
            aria-label="Toggle menu"
            onClick={toggleMobileMenu}
          >
            <i className="fas fa-bars"></i>
          </button>

          <ul className={`nav-menu ${mobileMenuOpen ? "active" : ""}`}>
            {error && (
              <li className="nav-error">
                <span title={error}>⚠️ Using fallback navigation</span>
              </li>
            )}
            {navigationData.length > 0 ? (
              navigationData.map((item) => renderNavItem(item))
            ) : (
              <li>
                <span>No navigation available</span>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default DynamicNavbar;
